import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { usePostMeterReasonCodes, useUpdateMeterReasonCodes, useMeterReadingStatusList } from "@features/mx/settings/hooks";
import { useGetResourceTypeList, useGetResourceSubtypeList } from "@features/mx/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";

interface AddConditionReasonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editData?: any;
}

export const AddConditionReasonModal: React.FC<AddConditionReasonModalProps> = ({
  open,
  onOpenChange,
  editData,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [watchResourceType, setWatchResourceType] = useState<string>("");

  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: resourceSubtypeResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined
  );

  const { data: statusListResponse } = useMeterReadingStatusList(remoteUtilityId, 1, 100, "");
  console.log('statusListResponse',statusListResponse)

  const resourceTypeOptions = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
          label: item.name || item,
          value: item.code || item,
        }))
      : [];
  }, [resourceTypeResponse]);

  const { toast } = useToast();
  const { mutate: createReason, isPending: isCreating } = usePostMeterReasonCodes();
  const { mutate: updateReason, isPending: isUpdating } = useUpdateMeterReasonCodes();

  const formFields: FormField[] = React.useMemo(() => [
    {
      name: "reasonName",
      label: "Reason Name",
      type: "text",
      required: true,
      placeholder: "e.g. Normal Read",
    },
    {
      name: "reasonCode",
      label: "Reason Code",
      type: "text",
      required: true,
      placeholder: "e.g. ooooo",
      className: "mt-4",
    },
    {
      name: "resourceType",
      label: "Resource Type",
      type: "select",
      required: false,
      placeholder: "Select resource type",
      options: resourceTypeOptions,
      className: "mt-4",
    },
    {
      name: "subType",
      label: "Sub Type",
      type: "select",
      required: false,
      placeholder: "Select sub type",
      disabled: (formValues) => !formValues.resourceType,
      options: (() => {
        const results = resourceSubtypeResponse?.result ?? [];
        if (!Array.isArray(results)) return [];
        return results.map((item: any) => ({
          label: item.hierarchyNames?.resourceSubtype || item.name || item,
          value: item.code || item,
        }));
      })(),
      className: "mt-4",
    },
    {
      name: "conditionStatus",
      label: "Condition Status",
      type: "select",
      required: true,
      placeholder: "Select condition status",
      options: (() => {
        const list = statusListResponse?.results || [];
        return list.map((item: any) => ({
          label: item.name,
          value: String(item.id),
        }));
      })(),
      className: "mt-4",
    },
  ], [resourceTypeOptions, resourceSubtypeResponse, statusListResponse, watchResourceType]);

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  const currentResourceType = form.watch("resourceType");
  useEffect(() => {
    setWatchResourceType(currentResourceType || "");
  }, [currentResourceType]);

  useEffect(() => {
    if (open) {
      if (editData) {
        const resType = editData.resourceTypeCode || editData.resource_type || "";
        const subTypeVal = editData.resourceSubtypeCode || editData.resource_subtype || "";
        setWatchResourceType(resType);
        form.reset({
          reasonName: editData.reason || editData.name || "",
          reasonCode: editData.reason_code || editData.reasonCode || "",
          resourceType: resType,
          subType: subTypeVal,
          conditionStatus: String(editData.statusId || editData.meterReadingStatus?.id || editData.meter_reading_status || ""),
        });
      } else {
        setWatchResourceType("");
        form.reset({
          reasonName: "",
          reasonCode: "",
          resourceType: "",
          subType: "",
          conditionStatus: "",
        });
      }
    }
  }, [open, editData]);

  const handleSubmit = (data: any) => {
    const payload = {
      meter_reading_status: data.conditionStatus,
      reason_code: data.reasonCode,
      reason: data.reasonName,
      remote_utility_id: String(remoteUtilityId),
      resource_type: data.resourceType || "",
      resource_subtype: data.subType || "",
    };

    if (editData) {
      updateReason(
        {
          id: Number(editData.id),
          payload: {
            ...payload,
            is_active: editData.is_active ?? true,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Condition reason updated successfully.",
            });
            queryClient.invalidateQueries({
              queryKey: ["mx", "settings", "meterReasonCodesList"],
            });
            onOpenChange(false);
          },
        }
      );
    } else {
      createReason(payload as any, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Condition reason added successfully.",
          });
          queryClient.invalidateQueries({
            queryKey: ["mx", "settings", "meterReasonCodesList"],
          });
          onOpenChange(false);
        },
      });
    }
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 mt-6">
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
        Cancel
      </Button>
      <Button type="submit" disabled={!form.formState.isValid}>
        {editData ? "Save Changes" : "Add Condition Reason"}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {editData ? "Edit Condition Reason" : "Add Condition Reason"}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              showSubmitButton: false,
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddConditionReasonModal;
